import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { taskKeys } from '../../tasks/queries/keys';
import { oneOffPaths } from '../../tasks/queries/paths';
import { tierKeys } from '../../tiers/queries/keys';
import { breakdownKeys } from '../queries/keys';
import { breakdownPaths } from '../queries/paths';

// TSS vite/03 — server state for the breakdown.
//
// NOTHING HERE IS OPTIMISTIC, and the three writes are the reason. Agreeing,
// proposing and authorizing are all decided by rules that live in the managers
// and in database triggers — a human session, the project's scope, whether
// every dependency is ready, whether the row is frozen. The browser cannot
// evaluate any of them. Painting a tier as agreed and rolling it back on the
// 409 would show a signature that never happened.

export function useBreakdown(tierId) {
    return useQuery({
        queryKey: breakdownKeys.tier(tierId),
        queryFn: ({ signal }) => api
            .get(breakdownPaths.breakdown(tierId), { signal })
            .then(r => r.data),
        enabled: !!tierId,
    });
}

export const PROPOSALS_PAGE_SIZE = 25;

export function useProposals(tierId, { state, limit = PROPOSALS_PAGE_SIZE, offset = 0 } = {}) {
    const params = { limit, offset };
    if (state) params.state = state;

    return useQuery({
        queryKey: breakdownKeys.proposals(tierId, params),
        queryFn: ({ signal }) => api
            .get(breakdownPaths.tasks(tierId), { params, signal })
            .then(r => r.data),
        enabled: !!tierId,
        placeholderData: keepPreviousData,
    });
}

// Agreeing. `request_id` and `breakdown_revision` are both required.
//
// WHO AND WHEN DO NOT TRAVEL: the backend takes them from the session and the
// clock. A panel that sent them would be offering to sign on somebody else's
// behalf, which is the one thing an agreement exists to make impossible.
export function useAgreeTier(tierId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.post(breakdownPaths.agreement(tierId), payload).then(r => r.data),
        onSuccess: () => invalidate(qc, tierId),
    });
}

// A batch of 1 to 50, in one transaction with its receipt inside. A failure —
// validation or a database error halfway through the write — leaves NO tasks,
// no edges and no receipt. So there is no partial success to render, and the
// screen must not invent one.
export function useProposeTasks(tierId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.post(breakdownPaths.proposals(tierId), payload).then(r => r.data),
        onSuccess: () => invalidate(qc, tierId),
    });
}

// ONE PROPOSAL, WITH ITS `spec_revision`.
//
// `GET /admin/tasks/{id}` answers a TaskDetailPackage that does NOT carry the
// specification revision, and authorizing without it is a 400 — or worse, a
// signature over a revision nobody read. The tier's breakdown list does carry
// it, with the doubts and the criteria, so the row is looked up there: pages of
// 100 (the cap), proposals only, until it appears.
//
// `null` means it is not a proposal any more (or not visible): the caller says
// so instead of offering a signature that the backend would refuse.
const PROPOSAL_SCAN_PAGE = 100;

export function useProposalRow(tierId, taskId, { enabled = true } = {}) {
    return useQuery({
        queryKey: breakdownKeys.proposal(tierId, taskId),
        queryFn: async ({ signal }) => {
            for (let offset = 0; ; offset += PROPOSAL_SCAN_PAGE) {
                const { data } = await api.get(breakdownPaths.tasks(tierId), {
                    params: { state: 'proposed', limit: PROPOSAL_SCAN_PAGE, offset },
                    signal,
                });
                const items = data?.items ?? [];
                const row = items.find(item => String(item.id) === String(taskId));
                if (row) return row;
                const total = data?.pagination?.total ?? 0;
                if (items.length === 0 || offset + items.length >= total) return null;
            }
        },
        enabled: enabled && !!tierId && !!taskId,
    });
}

// A ONE-OFF task, read by its own route: it has no tier, so the breakdown list
// that carries a planned task's `spec_revision` does not know it. Its detail
// carries the revision, the criteria and the doubts.
export function useOneOffRow(taskId, { enabled = true } = {}) {
    return useQuery({
        queryKey: ['tasks', 'one-off', String(taskId)],
        queryFn: ({ signal }) => api.get(oneOffPaths.detail(taskId), { signal }).then(r => r.data),
        enabled: enabled && !!taskId,
    });
}

// Authorizing a ONE-OFF task: its own route, the same body as `promote`
// (`request_id`, `spec_revision`, `priority` when it is being decided).
export function useAuthorizeOneOff(taskId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.post(oneOffPaths.authorization(taskId), payload).then(r => r.data),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: taskKeys.all });
            qc.invalidateQueries({ queryKey: ['events'] });
        },
    });
}

// Authorizing ONE task (`PATCH /admin/tasks/{id}/promote`): `request_id`,
// `spec_revision`, and `priority` only when the weight is being decided here.
export function usePromoteTask(taskId, tierId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.patch(breakdownPaths.promote(taskId), payload).then(r => r.data),
        onSuccess: () => {
            invalidate(qc, tierId);
            qc.invalidateQueries({ queryKey: ['events'] });
        },
    });
}

// Authorizing a selection. Atomic: if one row cannot be authorized, none is.
export function useAuthorizeTasks(tierId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.post(breakdownPaths.authorizations(tierId), payload).then(r => r.data),
        onSuccess: () => {
            invalidate(qc, tierId);
            // Every authorized task writes a `task.authorized` event.
            qc.invalidateQueries({ queryKey: ['events'] });
        },
    });
}

// One place. Forgetting the tier's own key is how a screen keeps showing
// `draft` after an agreement, and forgetting `tasks` is how the board keeps
// showing a task as a proposal after it was authorized.
function invalidate(qc, tierId) {
    qc.invalidateQueries({ queryKey: breakdownKeys.all });
    qc.invalidateQueries({ queryKey: tierKeys.detail(String(tierId)) });
    qc.invalidateQueries({ queryKey: tierKeys.all });
    qc.invalidateQueries({ queryKey: taskKeys.all });
}
